/* global theme */
(() => {
  const CART_HEADERS = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  // Gift wrap is generic per-cart now: at most one line, matched purely by
  // the _sst_gift_wrap property (no per-product linking).
  function findGiftLine(cart) {
    if (!cart || !cart.items) return null;
    return cart.items.find(i => i.properties && i.properties['_sst_gift_wrap'] === 'Yes') || null;
  }

  function notifyCartChanged() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  }

  // Last known gift-wrap state, applied synchronously to freshly-merged
  // boxes so they don't flash unchecked before the /cart.js resync below
  // confirms it — that flash was the "checkbox auto toggles" glitch.
  let lastKnownState = null;

  // In-flight cartAdd/remove promises, module-scoped (not per-box) because
  // the cart-drawer footer gets fully torn down and rebuilt on every cart
  // change, including the ones these requests themselves trigger — a
  // per-box-closure flag would be lost the moment the box it lives on gets
  // replaced mid-request.
  // addInFlight: the message `input` handler awaits it before patching
  // properties, so typing while an add is still in flight doesn't get
  // silently dropped (it used to fetch /cart.js immediately, find no
  // gift-wrap line yet since the add hadn't landed, and skip the patch —
  // losing whatever was typed in that window once the add's own stale,
  // earlier-captured message overwrote it).
  // removeInFlight: guards the /cart.js resync in syncBox. Without it, an
  // uncheck's own removal request could still be in flight when the cart
  // refresh it triggers re-runs that resync on the freshly-merged box, sees
  // the line still present server-side (removal hasn't committed yet), and
  // re-checks the box mid-removal — the "uncheck auto re-checks itself"
  // glitch.
  let addInFlight = null;
  let removeInFlight = null;

  function removeGiftLine(existing) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: CART_HEADERS,
      body: JSON.stringify({ id: existing.key, quantity: 0 }),
    });
  }

  function applyState(box, checked, message) {
    const cb = box.querySelector('.sst-cart-gift-cb');
    const msg = box.querySelector('.sst-cart-gift-msg');
    const label = box.querySelector('.sst-cart-gift-box');
    cb.checked = checked;
    label.classList.toggle('active', checked);
    // Skip the open/close transition here: this runs on a freshly-merged
    // (brand-new) textarea element re-applying already-known state, not a
    // user click — animating it would replay a close-then-open flash every
    // time the cart drawer footer re-renders after any cart change.
    msg.style.transition = 'none';
    msg.classList.toggle('open', checked);
    // eslint-disable-next-line no-unused-expressions
    msg.offsetHeight;
    msg.style.transition = '';
    if (checked && message) msg.value = message;
  }

  function syncBox(box) {
    const cb = box.querySelector('.sst-cart-gift-cb');
    const msg = box.querySelector('.sst-cart-gift-msg');
    const label = box.querySelector('.sst-cart-gift-box');
    const variantId = parseInt(box.dataset.giftVariant, 10);
    if (!cb || !variantId) return;

    if (lastKnownState) applyState(box, lastKnownState.checked, lastKnownState.message);

    // Reflect current cart state on load / drawer open. Also: if the
    // gift-wrap line is the ONLY line left in the cart (every real product
    // was removed), the gift-wrap line no longer makes sense on its own —
    // remove it too.
    if (!removeInFlight) {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          if (removeInFlight) return;
          const existing = findGiftLine(cart);
          if (!existing) {
            lastKnownState = { checked: false, message: '' };
            cb.checked = false;
            label.classList.remove('active');
            return;
          }
          if (cart.items.length === 1) {
            lastKnownState = { checked: false, message: '' };
            cb.checked = false;
            label.classList.remove('active');
            removeGiftLine(existing).then(notifyCartChanged);
            return;
          }
          const message = (existing.properties && existing.properties['Gift message']) || '';
          lastKnownState = { checked: true, message };
          cb.checked = true;
          label.classList.add('active');
          msg.value = message;
          msg.classList.add('open');
        });
    }

    cb.addEventListener('change', () => {
      const on = cb.checked;
      label.classList.toggle('active', on);
      msg.classList.toggle('open', on);

      if (on) {
        const giftTxt = msg.value.trim();
        const giftProps = { '_sst_gift_wrap': 'Yes' };
        if (giftTxt) giftProps['Gift message'] = giftTxt;
        lastKnownState = { checked: true, message: giftTxt };
        addInFlight = fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: CART_HEADERS,
          body: JSON.stringify({ id: variantId, quantity: 1, properties: giftProps }),
        })
          .then(notifyCartChanged)
          .catch(() => { window.SSTToast && window.SSTToast.show('Error adding gift wrap.'); })
          .then(() => { addInFlight = null; });
      } else {
        lastKnownState = { checked: false, message: lastKnownState ? lastKnownState.message : '' };
        removeInFlight = fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            const existing = findGiftLine(cart);
            if (!existing) return;
            return removeGiftLine(existing);
          })
          .then(notifyCartChanged)
          .catch(() => { window.SSTToast && window.SSTToast.show('Error removing gift wrap.'); })
          .then(() => { removeInFlight = null; });
      }
    });

    let msgTimeout;
    msg.addEventListener('input', () => {
      if (!cb.checked) return;
      if (lastKnownState) lastKnownState.message = msg.value.trim();
      clearTimeout(msgTimeout);
      msgTimeout = setTimeout(() => {
        // Wait for a checkbox-triggered add to land first, so the patch
        // below always finds the line and never races the add's own
        // (earlier-captured, now-stale) message.
        Promise.resolve(addInFlight).then(() => fetch('/cart.js'))
          .then(r => r.json())
          .then(cart => {
            const existing = findGiftLine(cart);
            if (!existing) return;
            const newProps = Object.assign({}, existing.properties);
            const giftTxt = msg.value.trim();
            if (giftTxt) newProps['Gift message'] = giftTxt;
            else delete newProps['Gift message'];
            // /cart/change.js re-keys the line (remove+add) since properties
            // are part of a line's identity. Deliberately NOT calling
            // notifyCartChanged() here: that would trigger a full cart-drawer
            // footer re-render (innerHTML swap), destroying and recreating
            // this exact textarea mid-type and resetting focus/cursor. The
            // line updates silently in the backend cart; nothing on screen
            // needs to change from a message edit (subtotal/discounts are
            // unaffected), so the next unrelated cart refresh will pick up
            // the new properties naturally.
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: CART_HEADERS,
              body: JSON.stringify({ id: existing.key, properties: newProps }),
            });
          })
          .catch(() => { window.SSTToast && window.SSTToast.show('Error updating gift message.'); });
      }, 500);
    });
  }

  function initAll() {
    document.querySelectorAll('.sst-cart-gift').forEach(box => {
      if (box.dataset.giftInit) return;
      box.dataset.giftInit = 'true';
      syncBox(box);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  // Cart drawer/page content gets replaced by the theme's own AJAX merge on
  // cart changes (see main.js CartForm.refreshFromHtml) — re-init afterwards
  // so newly-merged boxes reflect current cart state and stay interactive.
  document.addEventListener('on:cart:after-merge', initAll);
})();
